import React, { useMemo, useState } from 'react'
import { Container, Row, Col, Button, Popover, PopoverBody, Table } from 'reactstrap'
import { useQuery, gql } from '@apollo/client'
import { DateRange } from 'react-date-range'
import moment from 'moment'
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Legend } from 'recharts'
import Select from 'react-select'
import './IncidentReports.css'

const INCIDENT_REPORTS = gql`query IncidentReports($dateAddedGte: Date!, $dateAddedLte: Date!) {
    incidentCreationReport(dateAddedGte: $dateAddedGte, dateAddedLte: $dateAddedLte) {
        userName
        branchName
        count: incidentsCount
    }
    incidentUpdateReport(dateAddedGte: $dateAddedGte, dateAddedLte: $dateAddedLte) {
        userName
        branchName
        count: updatesCount
    }
    incidentDocumentReport(dateAddedGte: $dateAddedGte, dateAddedLte: $dateAddedLte) {
        userName
        branchName
        count: documentsCount
    }
    liabilityClaimReport(dateAddedGte: $dateAddedGte, dateAddedLte: $dateAddedLte) {
        userName
        branchName
        count: claimsCount
    }
}`
const COLORS = ['#2563EB', '#22C55E', '#F59E0B', '#8B5CF6', '#EC4899', '#14B8A6', '#6366F1', '#F97316', '#06B6D4', '#10B981', '#EF4444', '#A855F7', '#F43F5E', '#84CC16', '#0EA5E9', '#FB7185', '#60A5FA', '#FBBF24', '#34D399', '#C084FC', '#38BDF8', '#4ADE80', '#F472B6', '#FDBA74', '#93C5FD']
const formatDateLabel = (date) => moment(date).format('MMM D, YYYY')

const getDefaultRange = () => {
    const startDate = moment().startOf('week').toDate()
    const endDate = moment().endOf('week').toDate()
    return [{ startDate, endDate, key: 'selection' }]
}

const formatAxisTick = (value) => {
    if (!value) return 'Unknown'
    const label = String(value)
    return label
}

const renderTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null
    const rows = payload?.filter((item) => item && item.value > 0).map((item) => ({
        name: item.name || 'Unknown',
        value: item.value,
        color: item.color,
    })).sort((a, b) => b.value - a.value)
    if (!rows.length) return null
    return (
        <div className="incident-reports-tooltip">
            <div className="incident-reports-tooltip-title">{label}</div>
            <div className="incident-reports-tooltip-list">
                {rows.map((row) => (
                    <div key={row.name} className="incident-reports-tooltip-row">
                        <span className="incident-reports-tooltip-swatch" style={{ backgroundColor: row.color }} />
                        <span className="incident-reports-tooltip-branch">{row.name}</span>
                        <span className="incident-reports-tooltip-count">{row.value}</span>
                    </div>
                ))}
            </div>
        </div>
    )
}

const buildCreationReportData = (rows, filters) => {
    const queryData = rows.map((row) => ({
        userName: row.userName || 'Unknown',
        branchName: row.branchName || 'Unknown',
        incidentsCount: Number(row.count) || 0,
    }))

    const allUsersData = {}
    const allBranchesData = {}

    //calculate the total count of incidents for each user and branch across all the query data (before filtering)
    queryData?.forEach((row) => {
        allUsersData[row.userName] = (allUsersData[row.userName] || 0) + row.incidentsCount
        allBranchesData[row.branchName] = (allBranchesData[row.branchName] || 0) + row.incidentsCount
    })

    //Options to show in the filters
    const userOptions = Object.keys(allUsersData).map((name) => ({ label: name, value: name, }))
    const branchOptions = Object.keys(allBranchesData).map((name) => ({ label: name, value: name }))

    const filtered = queryData?.filter((row) => {
        if (filters.branches.length && !filters.branches.some((branch) => branch.value === row.branchName)) return false;
        if (filters.users.length && !filters.users.some((user) => user.value === row.userName)) return false;
        return true
    })

    const topUserName = Object.keys(allUsersData)?.reduce((a, b) => allUsersData[a] > allUsersData[b] ? a : b)
    const topBranchName = Object.keys(allBranchesData)?.reduce((a, b) => allBranchesData[a] > allBranchesData[b] ? a : b)

    const barChartData = Object.keys(allUsersData).map((userName) => ({
        userName: userName,
        ...Object.keys(allBranchesData).reduce((branchAcc, branch) => {
            branchAcc[branch] = filtered.find((row) => row.userName === userName && row.branchName === branch)?.incidentsCount || 0
            return branchAcc
        }, {}),
    }))

    const chartSeries = branchOptions.reduce((acc, branch, index) => {
        acc.push({ key: branch.value, label: branch.label, color: COLORS[index % COLORS.length] })
        return acc
    }, [])

    return {
        barChartData: barChartData,
        chartSeries: chartSeries,
        totalsByUser: allUsersData,
        totalsByBranch: allBranchesData,
        userOptions,
        branchOptions,
        summaryMetrics: {
            totalIncidents: queryData.reduce((sum, row) => sum + row.incidentsCount, 0),
            topBranchName,
            topBranchCount: allBranchesData[topBranchName] || 0,
            topUserName,
            topUserCount: allUsersData[topUserName] || 0,
        },
    }
}


const renderStackedBarChart = ({ data, series, xKey, height = 460 }) => (
    <>
        {(!data || !data.length) ? <div className="no-data">No data available for the selected date range.</div> :

            <ResponsiveContainer width="100%" height={height}>
                <BarChart data={data}>
                    <CartesianGrid strokeDasharray="2" vertical={false} />
                    <XAxis
                        padding={{ top: 10 }}
                        dataKey={xKey}
                        angle={-25}
                        textAnchor="end"
                        height={100}
                        interval={0}
                        tickFormatter={formatAxisTick}
                        tick={{ fontSize: 11 }}
                    />
                    <YAxis allowDecimals={false} />
                    <Tooltip content={renderTooltip} />
                    <Legend />
                    {series.map((item) => (
                        <Bar key={item.key} dataKey={item.key} name={item.label} stackId="stack" fill={item.color} />
                    ))}
                </BarChart>
            </ResponsiveContainer>
        }
    </>
)

const renderTwoLevelPieChart = ({ data }) => (
    <>
        {(!data || !data.length) ? <div className="no-data">No data available for the selected date range.</div> :
            <ResponsiveContainer width="100%" height={460}>
                <PieChart>
                    <Pie data={data[0]} dataKey="value" cx="50%" cy="50%" outerRadius="50%" fill="#8884d8" isAnimationActive={true} />
                    <Pie data={data[1]} dataKey="value" cx="50%" cy="50%" innerRadius="60%" outerRadius="80%" fill="#82ca9d" label isAnimationActive={true} />
                    <Tooltip defaultIndex={1} />
                    {/* <RechartsDevtools /> */}
                </PieChart>
            </ResponsiveContainer>
        }
    </>
)

const renderReportView = ({ title, activeView, chartType, chartData }) => {
    let { data, series, xKey } = chartData
    if (activeView === 'chart') {
        return (
            <div className="incident-reports-chart-surface">
                <div className="incident-reports-chart">
                    <h5>{title}</h5>
                    <br />
                    {chartType === 'bar' ? renderStackedBarChart({ data, series, xKey }) :
                        renderTwoLevelPieChart({ data })
                    }
                </div>
            </div>
        )
    }
    else if (activeView === 'table' && chartType === 'bar') {
        let tableData = {
            branchNames: series?.map((series) => series.key) || [],
            userNames: data?.map((row) => row[xKey]).filter(Boolean) || [],
            userBranchCounts: (data || []).reduce((acc, row) => {
                const userName = row[xKey]
                if (!userName) return acc
                acc[userName] = series.reduce((branchAcc, series) => {
                    branchAcc[series.key] = Number(row[series.key]) || 0
                    return branchAcc
                }, {})
                return acc
            }, {}),
        }
        return (
            <div className="incident-reports-chart-surface">
                <div className="incident-reports-chart">
                    <h5>{title}</h5>
                    <br />
                    <div className="incident-reports-table">
                        <Table responsive className="incident-reports-table-inner">
                            <thead>
                                <tr>
                                    {tableData.branchNames.length > 0 && <th>User</th>}
                                    {tableData.branchNames.map((branchName) => (
                                        <th key={branchName}>{branchName}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {tableData.userNames.map((userName) => {
                                    let branchCountByUser = tableData.userBranchCounts[userName] || {}
                                    return (
                                        <tr key={userName}>
                                            <td className="incident-reports-sticky-col">{userName}</td>
                                            {tableData.branchNames.map((branchName) => {
                                                let value = branchCountByUser[branchName] || 0
                                                let branchColor = series.find((s) => s.key === branchName)?.color || '#94a3b8'
                                                return (
                                                    <td key={branchName}>
                                                        <span className={value > 0 ? 'incident-reports-cell-highlight' : 'incident-reports-cell-zero'} style={value > 0 ? { border: `1px solid ${branchColor}`, color: branchColor, background: 'transparent' } : undefined}>
                                                            {value > 0 ? value : '-'}
                                                        </span>
                                                    </td>
                                                )
                                            })}
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </Table>
                    </div>
                    {tableData.branchNames.length > 0 && (
                        <div className="incident-reports-note">Tip: Scroll horizontally to view all branches.</div>
                    )}
                    {tableData.branchNames.length === 0 && (
                        <div className="incident-reports-status">No results for the selected date range.</div>
                    )}
                </div>
            </div>
        )
    }
}

const IncidentReports = () => {
    const [isDateRangeOpen, setIsDateRangeOpen] = useState(false)
    const [dateRangeSelection, setDateRangeSelection] = useState(getDefaultRange())
    const [activeView, setActiveView] = useState('chart')
    const [filters, setFilters] = useState({ users: [], branches: [] })

    const startDateIso = moment(dateRangeSelection[0].startDate).format('YYYY-MM-DD')
    const endDateIso = moment(dateRangeSelection[0].endDate).format('YYYY-MM-DD')

    const { data: reportData, loading, error } = useQuery(INCIDENT_REPORTS, {
        variables: { dateAddedGte: startDateIso, dateAddedLte: endDateIso },
    })

    const { barChartData, chartSeries, summaryMetrics, totalsByUser, totalsByBranch, userOptions, branchOptions } = useMemo(() => {
        const creationReportData = reportData?.incidentCreationReport?.length ? reportData.incidentCreationReport : []
        if (creationReportData.length === 0) {
            return {
                barChartData: [],
                chartSeries: [],
                summaryMetrics: { totalIncidents: 0, topBranchName: '', topBranchCount: 0, topUserName: '', topUserCount: 0 },
                totalsByUser: {},
                totalsByBranch: {},
                userOptions: [],
                branchOptions: [],
            }
        }
        return buildCreationReportData(creationReportData, filters)
    }, [reportData, filters])

    const { barChartData: updateBarChartData, chartSeries: updateChartSeries } = useMemo(() => {
        const updateReportData = reportData?.incidentUpdateReport?.length ? reportData.incidentUpdateReport : []
        if (updateReportData.length === 0) {
            return {
                barChartData: [],
                chartSeries: [],
            }
        }
        return buildCreationReportData(updateReportData, filters)
    }, [reportData, filters])

    const { barChartData: documentBarChartData, chartSeries: documentChartSeries } = useMemo(() => {
        const documentReportData = reportData?.incidentDocumentReport?.length ? reportData.incidentDocumentReport : []
        if (documentReportData.length === 0) {
            return {
                barChartData: [],
                chartSeries: [],
            }
        }
        return buildCreationReportData(documentReportData, filters)
    }, [reportData, filters])

    const { barChartData: liabilityBarChartData, chartSeries: liabilityChartSeries } = useMemo(() => {
        const liabilityReportData = reportData?.liabilityClaimReport?.length ? reportData.liabilityClaimReport : []
        if (liabilityReportData.length === 0) {
            return {
                barChartData: [],
                chartSeries: [],
            }
        }
        return buildCreationReportData(liabilityReportData, filters)
    }, [reportData, filters])

    return (
        <Container fluid className="incident-reports-container">
            <Row className="incident-reports-header">
                <Col xs="12" md="6" className="incident-reports-title">
                    <h4>Incident Reports</h4>
                    <p>Incident activity breakdown by user and branch</p>
                </Col>
                <Col xs="12" md="6" className="incident-reports-filter">
                    <Button id="incidentReportsDateRange" className="incident-reports-date-button" onClick={() => setIsDateRangeOpen(!isDateRangeOpen)}>
                        <i className="fa fa-calendar" aria-hidden="true"></i>
                        {formatDateLabel(dateRangeSelection[0].startDate)} - {formatDateLabel(dateRangeSelection[0].endDate)}
                    </Button>
                    <Popover trigger="legacy" placement="bottom" isOpen={isDateRangeOpen} target="incidentReportsDateRange" toggle={() => setIsDateRangeOpen(!isDateRangeOpen)}>
                        <PopoverBody className="incident-reports-popover">
                            <DateRange ranges={dateRangeSelection} onChange={(ranges) => setDateRangeSelection([ranges.selection])} editableDateInputs={true} moveRangeOnFirstSelection={false} />
                        </PopoverBody>
                    </Popover>
                </Col>
            </Row>

            <div className="incident-reports-kpis">
                <div className="incident-reports-kpi-card">
                    <div className="incident-reports-kpi-header">
                        <span>Total Incidents</span>
                        <span className="incident-reports-kpi-icon">
                            <i className="fa fa-info-circle" aria-hidden="true"></i>
                        </span>
                    </div>
                    <div className="incident-reports-kpi-value">{summaryMetrics.totalIncidents}</div>
                    <div className="incident-reports-kpi-subtext">Incidents in selected range</div>
                </div>

                <div className="incident-reports-kpi-card">
                    <div className="incident-reports-kpi-header">
                        <span>Top Branch</span>
                        <span className="incident-reports-kpi-icon purple">
                            <i className="fa fa-bar-chart" aria-hidden="true"></i>
                        </span>
                    </div>
                    <div className="incident-reports-kpi-value">{summaryMetrics.topBranchCount}</div>
                    <div className="incident-reports-kpi-subtext">{summaryMetrics.topBranchName}</div>
                </div>

                <div className="incident-reports-kpi-card highlight">
                    <div className="incident-reports-kpi-header">
                        <span>Most Active User</span>
                        <span className="incident-reports-kpi-icon green">
                            <i className="fa fa-users" aria-hidden="true"></i>
                        </span>
                    </div>
                    <div className="incident-reports-kpi-value">{summaryMetrics.topUserCount}</div>
                    <div className="incident-reports-kpi-subtext">{summaryMetrics.topUserName}</div>
                </div>
            </div>

            <Row>
                <Col xs="12" className="incident-reports-card">
                    <div className="incident-reports-panel-header">
                        <div className="incident-reports-view-toggle">
                            <button type="button" className={activeView === 'chart' ? 'active' : ''} onClick={() => setActiveView('chart')}>
                                <i className="fa fa-bar-chart" aria-hidden="true"></i>
                                Chart View
                            </button>
                            <button type="button" className={activeView === 'table' ? 'active' : ''} onClick={() => setActiveView('table')}>
                                <i className="fa fa-table" aria-hidden="true"></i>
                                Table View
                            </button>
                        </div>
                        <div className="incident-reports-filters">
                            <div className="incident-reports-select">
                                <Select
                                    classNamePrefix="incident-reports-select"
                                    placeholder="All users"
                                    value={filters.users}
                                    onChange={(value) =>
                                        setFilters((prev) => ({
                                            ...prev,
                                            users: value || [],
                                        }))
                                    }
                                    options={userOptions}
                                    isMulti
                                    isClearable
                                />
                            </div>
                            <div className="incident-reports-select">
                                <Select
                                    classNamePrefix="incident-reports-select"
                                    placeholder="All branches"
                                    value={filters.branches}
                                    onChange={(value) =>
                                        setFilters((prev) => ({
                                            ...prev,
                                            branches: value || [],
                                        }))
                                    }
                                    options={branchOptions}
                                    isMulti
                                    isClearable
                                />
                            </div>
                        </div>
                    </div>
                    {loading && <div className="incident-reports-status">Loading report...</div>}
                    {!loading && error && (
                        <div className="incident-reports-status error">Unable to load report. Please try again.</div>
                    )}
                    {!loading && !error && (
                        <>
                            <Row>
                                <Col xs={activeView === 'table' ? 12 : 6}>
                                    {renderReportView({
                                        title: 'Incident Creation Summary',
                                        activeView,
                                        chartType: 'pie',
                                        chartData: {
                                            data: [Object.keys(totalsByUser)?.map(user => ({ name: user, value: totalsByUser[user] })), Object.keys(totalsByBranch)?.map(branch => ({ name: branch, value: totalsByBranch[branch] }))],
                                        },
                                    })}
                                </Col>
                                <Col xs={activeView === 'table' ? 12 : 6}>
                                    {renderReportView({
                                        title: 'Incident Creation by User',
                                        activeView,
                                        chartType: 'bar',
                                        chartData: {
                                            data: barChartData,
                                            series: chartSeries,
                                            xKey: 'userName',
                                        },
                                    })}
                                </Col>
                            </Row>
                            <Row>
                                <Col xs={activeView === 'table' ? 12 : 6}>
                                    {renderReportView({
                                        title: 'Incident Updates by User',
                                        activeView,
                                        chartType: 'bar',
                                        chartData: {
                                            data: updateBarChartData,
                                            series: updateChartSeries,
                                            xKey: 'userName',
                                        },
                                    })}
                                </Col>
                                <Col xs={activeView === 'table' ? 12 : 6}>
                                    {renderReportView({
                                        title: 'Documents Uploaded by User',
                                        activeView,
                                        chartType: 'bar',
                                        chartData: {
                                            data: documentBarChartData,
                                            series: documentChartSeries,
                                            xKey: 'userName',
                                        },
                                    })}
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    {renderReportView({
                                        title: 'Liability Claims Added by User',
                                        activeView,
                                        chartType: 'bar',
                                        chartData: {
                                            data: liabilityBarChartData,
                                            series: liabilityChartSeries,
                                            xKey: 'userName',
                                        },
                                    })}
                                </Col>
                            </Row>
                        </>
                    )}
                    {!loading && !error && activeView === 'chart' && barChartData.length === 0 && (
                        <div className="incident-reports-status">No results for the selected date range.</div>
                    )}
                </Col>
            </Row>
        </Container>
    )
}

export default IncidentReports
